import * as THREE from "three";
import { getAssetById } from "../../stores/assetStore";
import { getSceneActions, getSceneState } from "../../stores/sceneStore";
import { getSceneBehaviorContextStoreActions } from "../../stores/sceneBehaviorContextStore";
import { loadSceneRecipeById } from "../../utils/sceneRecipes";
import { placeLibraryObject, placeMeshAsset } from "../../utils/objectLibraryPlacement";
import { insertUserLibraryObjectBundle } from "../../utils/userLibraryObjectBundle";
import { sampleMaxHFieldHeightInModelBounds } from "../../utils/hfieldModelSampling";
import { getStandardFloorHField } from "../../utils/terrainFloor";
import { debugLibraryDrop } from "../scene/sceneConstants";

export function resetLibraryDropRefs({
  libraryDragDataRef,
  libraryDragPointerRef,
  libraryDragStartPointerRef,
  libraryLastValidDropPointRef,
  libraryLastValidDropSupportZRef,
  libraryLastValidDropSupportRef,
}) {
  libraryDragDataRef.current = null;
  libraryDragPointerRef.current = null;
  libraryDragStartPointerRef.current = null;
  libraryLastValidDropPointRef.current = null;
  libraryLastValidDropSupportZRef.current = null;
  libraryLastValidDropSupportRef.current = null;
}

export async function handleLibraryDrop(dragData, worldPosition, attachOptions = {}) {
  if (!dragData) return null;
  const onProgress = attachOptions.onProgress;
  if (dragData.itemType === "presetScene" && dragData.recipeId) {
    try {
      await loadSceneRecipeById(dragData.recipeId, { onProgress });
      getSceneBehaviorContextStoreActions().setFromPresetRecipeId(dragData.recipeId);
    } catch (error) {
      getSceneBehaviorContextStoreActions().clear();
      throw error;
    }
    const worldBodies = getSceneState().worldBodies || [];
    const latestWorldBody = [...worldBodies].pop();
    const uuid = typeof latestWorldBody === "string" ? latestWorldBody : latestWorldBody?.uuid;
    return uuid || null;
  }
  if (dragData.itemType === "libraryObject" && dragData.object) {
    const opts = {};
    if (Array.isArray(worldPosition) && worldPosition.length === 3) {
      opts.position = worldPosition;
    }
    if (attachOptions.robotAttachmentSiteUuid) {
      opts.robotAttachmentSiteUuid = attachOptions.robotAttachmentSiteUuid;
    }
    if (typeof onProgress === "function") {
      opts.onProgress = onProgress;
    }
    const result = await placeLibraryObject(dragData.object, opts);
    return result?.rootBodyUuid || null;
  }
  if (dragData.itemType === "userLibraryObject") {
    const bundle = dragData.objectBundle || dragData.item?.metadata?.objectBundle;
    const insertedRootUuid = insertUserLibraryObjectBundle(bundle);
    if (!insertedRootUuid) return null;
    if (Array.isArray(worldPosition) && worldPosition.length === 3) {
      const body = getSceneState().bodies?.[insertedRootUuid];
      const orientation =
        Array.isArray(body?.orientation) && body.orientation.length === 4
          ? body.orientation
          : [1, 0, 0, 0];
      getSceneActions().setBodyTransform(insertedRootUuid, worldPosition, orientation);
    }
    return insertedRootUuid;
  }
  if (dragData.itemType === "meshAsset") {
    if (!Array.isArray(worldPosition) || worldPosition.length !== 3) return null;
    const meshEntry = dragData.entry || (dragData.entryId ? getAssetById(dragData.entryId) : null);
    if (!meshEntry || meshEntry.kind !== "mesh") return null;
    onProgress?.(0.15);
    const result = await placeMeshAsset(meshEntry, { position: worldPosition });
    onProgress?.(1);
    return result?.insertedBodyUuid || null;
  }
  return null;
}

export async function waitForSceneObjectRef(sceneObjectsRef, bodyUuid, maxFrames = 12) {
  if (!bodyUuid) return null;
  for (let frame = 0; frame < maxFrames; frame += 1) {
    const obj = sceneObjectsRef.current?.[bodyUuid];
    if (obj) {
      debugLibraryDrop("wait: body ref ready", { bodyUuid, frame });
      return obj;
    }
    await new Promise((resolve) => window.requestAnimationFrame(() => resolve()));
  }
  const obj = sceneObjectsRef.current?.[bodyUuid] || null;
  debugLibraryDrop("wait: body ref final", { bodyUuid, found: !!obj });
  return obj;
}

export function ensureBodyBoundsAboveSupport({
  bodyUuid,
  support = 0,
  modelGroupRef,
  sceneObjectsRef,
}) {
  if (!bodyUuid || !modelGroupRef.current) return false;
  const supportInfo =
    support && typeof support === "object"
      ? support
      : { z: Number.isFinite(Number(support)) ? Number(support) : 0 };
  let supportZ = Number.isFinite(Number(supportInfo.z)) ? Number(supportInfo.z) : 0;
  const bodyObj = sceneObjectsRef.current?.[bodyUuid];
  const renderBodyObj = sceneObjectsRef.current?.[`b_${bodyUuid}`];
  const bodyObjects = [bodyObj, renderBodyObj].filter(Boolean);
  if (bodyObjects.length === 0) {
    debugLibraryDrop("bounds: missing body object", { bodyUuid });
    return false;
  }

  const worldBounds = new THREE.Box3();
  let hasGeomBounds = false;
  const isRenderableGeomObject = (object) => {
    let cur = object;
    while (cur) {
      const type = cur.userData?.type;
      if (type === "mesh" || type === "collision" || type === "visual") return true;
      cur = cur.parent;
    }
    return false;
  };
  bodyObjects.forEach((obj) => {
    obj.updateWorldMatrix(true, true);
    obj.traverse((child) => {
      if (!child?.isMesh || !child.visible || !child.geometry) return;
      if (!isRenderableGeomObject(child)) return;
      const geometry = child.geometry;
      if (!geometry.boundingBox) {
        geometry.computeBoundingBox?.();
      }
      if (!geometry.boundingBox) return;
      const meshBounds = geometry.boundingBox.clone().applyMatrix4(child.matrixWorld);
      if (!hasGeomBounds) {
        worldBounds.copy(meshBounds);
        hasGeomBounds = true;
      } else {
        worldBounds.union(meshBounds);
      }
    });
  });
  if (!hasGeomBounds || worldBounds.isEmpty()) {
    debugLibraryDrop("bounds: no geom bounds", {
      bodyUuid,
      hasGeomBounds,
      isEmpty: worldBounds.isEmpty(),
    });
    return false;
  }

  const corners = [
    new THREE.Vector3(worldBounds.min.x, worldBounds.min.y, worldBounds.min.z),
    new THREE.Vector3(worldBounds.min.x, worldBounds.min.y, worldBounds.max.z),
    new THREE.Vector3(worldBounds.min.x, worldBounds.max.y, worldBounds.min.z),
    new THREE.Vector3(worldBounds.min.x, worldBounds.max.y, worldBounds.max.z),
    new THREE.Vector3(worldBounds.max.x, worldBounds.min.y, worldBounds.min.z),
    new THREE.Vector3(worldBounds.max.x, worldBounds.min.y, worldBounds.max.z),
    new THREE.Vector3(worldBounds.max.x, worldBounds.max.y, worldBounds.min.z),
    new THREE.Vector3(worldBounds.max.x, worldBounds.max.y, worldBounds.max.z),
  ];
  let minLocalZ = Infinity;
  const modelBounds = new THREE.Box3();
  corners.forEach((corner) => {
    const localCorner = corner.clone();
    modelGroupRef.current.worldToLocal(localCorner);
    if (localCorner.z < minLocalZ) minLocalZ = localCorner.z;
    modelBounds.expandByPoint(localCorner);
  });

  if (supportInfo.objectName === "sceneFloor" && !modelBounds.isEmpty()) {
    const floorHField = getStandardFloorHField(getSceneState()?.hfields);
    const terrainHeight = sampleMaxHFieldHeightInModelBounds(floorHField, modelBounds);
    if (terrainHeight != null && Number.isFinite(terrainHeight)) {
      supportZ = Math.max(supportZ, terrainHeight);
    }
  }

  debugLibraryDrop("bounds: measured", {
    bodyUuid,
    supportInfo,
    supportZ,
    minLocalZ,
    modelBoundsMin: modelBounds.min.toArray(),
    modelBoundsMax: modelBounds.max.toArray(),
  });
  if (!Number.isFinite(minLocalZ)) return false;
  if (minLocalZ >= supportZ) {
    debugLibraryDrop("bounds: no lift needed", { bodyUuid, minLocalZ, supportZ });
    return true;
  }
  const body = getSceneState().bodies?.[bodyUuid];
  if (!body || !Array.isArray(body.position) || body.position.length !== 3) {
    debugLibraryDrop("bounds: missing store body position", { bodyUuid, hasBody: !!body });
    return false;
  }
  const nextPosition = [
    body.position[0],
    body.position[1],
    body.position[2] + (supportZ - minLocalZ) + 0.001,
  ];
  const nextOrientation =
    Array.isArray(body.orientation) && body.orientation.length === 4
      ? body.orientation
      : [1, 0, 0, 0];
  debugLibraryDrop("bounds: lifting", {
    bodyUuid,
    from: body.position,
    to: nextPosition,
    deltaZ: nextPosition[2] - body.position[2],
  });
  getSceneActions().setBodyTransform(bodyUuid, nextPosition, nextOrientation);
  return true;
}
